import type {
  BoardItem,
  CombineResult,
  ModelInfo,
  Profile,
  ProfileScope,
  ProfileState,
  SpendSummary,
  User,
} from "@fusioncraft/shared";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      ...init,
    });
  } catch {
    throw new ApiError(0, "Can't reach the server. Check your connection.");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status}).`, data.code);
  return data as T;
}

export const api = {
  me: () => request<{ user: User | null }>("GET", "/auth/me"),
  signupStatus: () => request<{ open: boolean }>("GET", "/auth/signup"),
  register: (email: string, password: string, inviteCode: string) =>
    request<{ user: User }>("POST", "/auth/register", { email, password, inviteCode }),
  login: (email: string, password: string, code?: string) =>
    request<{ user: User }>("POST", "/auth/login", { email, password, code }),
  logout: () => request<{ ok: true }>("POST", "/auth/logout", {}),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>("POST", "/auth/password", { currentPassword, newPassword }),
  totpSetup: () => request<{ secret: string; uri: string; qr: string }>("POST", "/auth/2fa/setup", {}),
  totpEnable: (code: string) => request<{ user: User }>("POST", "/auth/2fa/enable", { code }),
  totpDisable: (password: string, code: string) => request<{ user: User }>("POST", "/auth/2fa/disable", { password, code }),

  /** Spend for the local today / month / year and all time. */
  spend: () => {
    const now = new Date();
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const month = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const year = new Date(now.getFullYear(), 0, 1).getTime();
    return request<SpendSummary>("GET", `/spend?day=${day}&month=${month}&year=${year}`);
  },
  models: () => request<{ models: ModelInfo[]; defaultModel: string }>("GET", "/models"),
  profiles: () => request<{ profiles: Profile[] }>("GET", "/profiles"),
  createProfile: (name: string, model: string, scope: ProfileScope) =>
    request<{ profile: Profile }>("POST", "/profiles", { name, model, scope }),
  renameProfile: (id: string, name: string) => request<{ profile: Profile }>("PATCH", `/profiles/${id}`, { name }),
  deleteProfile: (id: string) => request<{ ok: true }>("DELETE", `/profiles/${id}`),
  state: (id: string) => request<ProfileState>("GET", `/profiles/${id}`),
  resetProfile: (id: string) => request<ProfileState>("POST", `/profiles/${id}/reset`, {}),
  saveBoard: (id: string, board: BoardItem[], keepalive = false) =>
    request<{ ok: true }>("PUT", `/profiles/${id}/board`, { board }, { keepalive }),
  combine: (id: string, a: number, b: number) => request<CombineResult>("POST", `/profiles/${id}/combine`, { a, b }),
};
